import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Breadcrumb multi-nível do Mini App, espelhando o `navStack` do bot
 * (ver `packages/bot/src/handlers/nav.ts`). Persistido em `sessionStorage`
 * pra sobreviver a remounts do Telegram WebView quando o usuário esconde/abre.
 *
 * Regras (alinhadas com o bot):
 * - Profundidade máxima: 6 — `push` além disso descarta o item mais antigo.
 * - `push` ignora duplicatas consecutivas (mesmo path empurrado de novo).
 * - `reset` zera quando o usuário volta pra `/` (home).
 *
 * Layout:
 *   <BrowserRouter>
 *     <NavStackProvider>
 *       <Routes>...</Routes>
 *     </NavStackProvider>
 *   </BrowserRouter>
 */

export interface NavStackEntry {
  /** URL completa (path + search). */
  path: string;
  /** Label opcional pra `<NavBar>` (default: último segmento do path). */
  label?: string;
}

interface NavStackValue {
  stack: NavStackEntry[];
  /** Profundidade atual (>= 1 quando dentro de qualquer rota). */
  depth: number;
  push(entry: NavStackEntry): void;
  replace(entry: NavStackEntry): void;
  pop(): void;
  reset(entry?: NavStackEntry): void;
  /** Volta um nível via router; se a pilha tem 1 item, vai pra `/`. */
  back(): void;
  /** Volta direto pra raiz (`/`) e zera a pilha. */
  home(): void;
}

const NAV_STACK_CAP = 6;
const STORAGE_KEY = 'torian.navStack.v1';

const NavStackContext = createContext<NavStackValue | null>(null);

function loadInitial(currentPath: string): NavStackEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [{ path: currentPath }];
    const parsed = JSON.parse(raw) as NavStackEntry[];
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [{ path: currentPath }];
    }
    // Se a rota atual diverge do topo, dropa a stale stack — usuário
    // provavelmente abriu um deep-link novo a partir do bot.
    if (parsed[parsed.length - 1].path !== currentPath) {
      return [{ path: currentPath }];
    }
    return parsed.slice(-NAV_STACK_CAP);
  } catch {
    return [{ path: currentPath }];
  }
}

export function NavStackProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname + location.search;

  const [stack, setStack] = useState<NavStackEntry[]>(() => loadInitial(currentPath));

  // Persiste em sessionStorage a cada mudança.
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stack));
    } catch {
      /* sessionStorage indisponível (raro no Telegram) — ignora. */
    }
  }, [stack]);

  // Mantemos `stack` em ref pra evitar dependency churn nos callbacks abaixo.
  const stackRef = useRef(stack);
  useEffect(() => {
    stackRef.current = stack;
  }, [stack]);

  const push = useCallback((entry: NavStackEntry) => {
    setStack((s) => {
      const top = s[s.length - 1];
      if (top?.path === entry.path) return s;
      const next = [...s, entry];
      return next.length > NAV_STACK_CAP
        ? next.slice(next.length - NAV_STACK_CAP)
        : next;
    });
  }, []);

  const replace = useCallback((entry: NavStackEntry) => {
    setStack((s) => {
      if (s.length === 0) return [entry];
      const next = [...s];
      next[next.length - 1] = entry;
      return next;
    });
  }, []);

  const pop = useCallback(() => {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);

  const reset = useCallback((entry?: NavStackEntry) => {
    setStack(entry ? [entry] : []);
  }, []);

  const back = useCallback(() => {
    const s = stackRef.current;
    if (s.length > 1) {
      const target = s[s.length - 2];
      setStack(s.slice(0, -1));
      navigate(target.path);
    } else {
      navigate('/');
    }
  }, [navigate]);

  const home = useCallback(() => {
    setStack([{ path: '/' }]);
    navigate('/');
  }, [navigate]);

  const value = useMemo<NavStackValue>(
    () => ({ stack, depth: stack.length, push, replace, pop, reset, back, home }),
    [stack, push, replace, pop, reset, back, home],
  );

  return <NavStackContext.Provider value={value}>{children}</NavStackContext.Provider>;
}

export function useNavStack(): NavStackValue {
  const ctx = useContext(NavStackContext);
  if (!ctx) {
    throw new Error('useNavStack must be used inside <NavStackProvider>');
  }
  return ctx;
}

/**
 * Açucar pra telas: registra a rota atual na pilha no mount com o `label`
 * passado. Usa `push` por default; passe `mode: 'replace'` em telas de
 * "edição" (sign-order, sub-passos) que substituem a anterior.
 */
export function useNavStackEntry(
  entry: Omit<NavStackEntry, 'path'> & { mode?: 'push' | 'replace' | 'reset' },
): void {
  const { push, replace, reset } = useNavStack();
  const location = useLocation();
  const path = location.pathname + location.search;
  const mode = entry.mode ?? 'push';

  useEffect(() => {
    const e: NavStackEntry = { path, label: entry.label };
    if (mode === 'replace') replace(e);
    else if (mode === 'reset') reset(e);
    else push(e);
    // Só dispara quando muda o path real, não em cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
}
